import {
  useQuery,
  useMutation,
  UseQueryOptions,
  QueryKey,
  UseMutationOptions,
} from 'react-query'
import api from 'api'

import {
  ListProperty,
  UserFavoritesListDTO,
  PropertyAd,
  AdminPropertyAd,
} from 'types'

import {
  PaginationOptions,
  FilterOptions,
  UserFavoritesListDetails,
  DeleteFavoriteDTO,
  DeleteFavoriteListDTO,
  UserFavoriteDTO,
  UpdateUserFavoriteListDTO,
} from './types'
import { AxiosError, AxiosResponse } from 'axios'

import { SortingCategoryEnum, SortingOrderEnum } from 'types/enums'

const CONTROLLER_PATH = '/app/spourgiti/admin/user-favorites/lists'

const MutationKeys = {
  count: ['countByCriteria'],
  search: ['searchByCriteria'],
  userFavoritesList: ['getUserFavoritesList'],
  addToFavoritesLists: ['addToFavoritesLists'],
  userFavoritesListPropertyAds: ['getUserFavoritesListPropertyAds'],
  removeFromFavoritesLists: ['removeFromUserFavoritesList'],
  removeFavoritesList: ['removeFavoritesList'],
  updateList: 'updateList',
} as const

const QueryKeys = {
  listSingle: 'listSingle',
  listPropertyAds: 'listPropertyAds',
} as const

//Count all mutation

export const countByCriteria = async (
  searchOptions: Partial<FilterOptions>
): Promise<number> => {
  const { data }: AxiosResponse<number> = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/count-by-criteria`,
    data: searchOptions,
  })
  return data
}

export const useCountByCriteriaMutation = (
  options: Omit<
    UseMutationOptions<number, AxiosError, Partial<FilterOptions>>,
    'mutationKey' | 'mutationFn'
  > = {}
) =>
  useMutation(MutationKeys.count, countByCriteria, {
    ...options,
  } as Omit<UseMutationOptions<number, AxiosError, Partial<FilterOptions>>, 'mutationKey' | 'mutationFn'>)

// Search by criteria
export const searchByCriteria = async (
  searchOptions: Partial<PaginationOptions>
): Promise<UserFavoritesListDTO> => {
  const { data }: AxiosResponse<UserFavoritesListDTO> = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/search-by-criteria`,
    data: searchOptions,
  })
  return data
}

export const useSearchByCriteriaMutation = (
  options: Omit<
    UseMutationOptions<
      UserFavoritesListDTO,
      AxiosError,
      Partial<PaginationOptions>
    >,
    'mutationKey' | 'mutationFn'
  > = {}
) =>
  useMutation(MutationKeys.search, searchByCriteria, {
    ...options,
  } as Omit<UseMutationOptions<UserFavoritesListDTO, AxiosError, Partial<PaginationOptions>>, 'mutationKey' | 'mutationFn'>)

//get user fav list properties
export const getUserFavoritesListPropertyAds = async (
  searchOptions: Partial<UserFavoritesListDetails>
): Promise<AdminPropertyAd[]> => {
  const { data } = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/get-propertyAds`,
    data: searchOptions,
  })
  return data
}

export const useListPropertyAds = <T = AdminPropertyAd[]>(
  listId: number,
  pageNumber: number,
  pageSize: number,
  sorting: Partial<{
    sortingOrder: SortingOrderEnum
    sortingCategory: SortingCategoryEnum
  }> = {},
  options: UseQueryOptions<AdminPropertyAd[], AxiosError, T> = {}
) => {
  return useQuery<AdminPropertyAd[], AxiosError, T>(
    [QueryKeys.listPropertyAds, listId, pageNumber, pageSize, sorting],
    () =>
      getUserFavoritesListPropertyAds({
        pageNumber,
        pageSize,
        listId,
        ...sorting,
      }),
    {
      ...options,
    } as Omit<
      UseQueryOptions<AdminPropertyAd[], AxiosError, T, QueryKey>,
      'queryKey' | 'queryFn'
    >
  )
}

export const useGetUserFavoritesListPropertyAdsMutation = (
  options: Omit<
    UseMutationOptions<
      AdminPropertyAd[],
      AxiosError,
      Partial<UserFavoritesListDetails>
    >,
    'mutationKey' | 'mutationFn'
  > = {}
) =>
  useMutation(
    MutationKeys.userFavoritesListPropertyAds,
    getUserFavoritesListPropertyAds,
    {
      ...options,
    }
  )

//get user fav list detail
export const getUserFavoritesList = async (
  listId: number
): Promise<ListProperty> => {
  const { data }: AxiosResponse<ListProperty> = await api({
    method: 'get',
    url: `${CONTROLLER_PATH}/${listId}`,
  })
  return data
}

export const useGetUserFavoritesListMutation = (
  options: Omit<
    UseMutationOptions<ListProperty, AxiosError, UserFavoritesListDetails>,
    'mutationKey' | 'mutationFn'
  > = {}
) =>
  useMutation(...MutationKeys.userFavoritesList, getUserFavoritesList, {
    ...options,
  } as Omit<UseMutationOptions<ListProperty, AxiosError, number>, 'mutationKey' | 'mutationFn'>)

export const useList = <T = ListProperty>(
  id: number,
  options: UseQueryOptions<T, AxiosError, ListProperty> = {}
) => {
  return useQuery([QueryKeys.listSingle, id], () => getUserFavoritesList(id), {
    ...options,
  } as Omit<UseQueryOptions<ListProperty, AxiosError, ListProperty, QueryKey>, 'queryKey' | 'queryFn'>)
}

//Update ListProperty
export const updateList = async (
  payload: Partial<UpdateUserFavoriteListDTO>
): Promise<ListProperty> => {
  const { data } = await api({
    method: 'POST',
    url: `${CONTROLLER_PATH}`,
    data: payload,
  })
  return data
}
export const useUpdateList = (
  options: Omit<
    UseMutationOptions<ListProperty, AxiosError, UpdateUserFavoriteListDTO>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.addToFavoritesLists, updateList, {
    ...options,
  } as Omit<UseMutationOptions<ListProperty, AxiosError, UpdateUserFavoriteListDTO>, 'mutationKey' | 'mutationFn'>)
}

//POST Update favorites list property
export const addToUserFavoritesList = async (
  payload: Partial<UserFavoriteDTO>
): Promise<PropertyAd> => {
  const { data } = await api({
    method: 'POST',
    url: `${CONTROLLER_PATH}/propertyAd`,
    data: payload,
  })
  return data
}
export const useAddToUserFavoritesList = (
  options: Omit<
    UseMutationOptions<PropertyAd, AxiosError, Partial<UserFavoriteDTO>>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.addToFavoritesLists, addToUserFavoritesList, {
    ...options,
  })
}
//Delete a listing
export const removeFromUserFavoritesList = async (
  options: DeleteFavoriteDTO
): Promise<Response> => {
  const { data }: AxiosResponse<Response> = await api({
    method: 'delete',
    url: `${CONTROLLER_PATH}/${options.listId}/${options.propertyAdAlternativeId}`,
  })
  return data
}
export const useRemoveFromUserFavoritesListMutation = (
  options: Omit<
    UseMutationOptions<Response, AxiosError, DeleteFavoriteDTO>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(
    MutationKeys.removeFromFavoritesLists,
    removeFromUserFavoritesList,
    {
      ...options,
    } as Omit<
      UseMutationOptions<Response, AxiosError, DeleteFavoriteDTO>,
      'mutationKey' | 'mutationFn'
    >
  )
}

//delete whole list

export const deleteUserFavoritesList = async (
  DeleteFavoriteListDTO
): Promise<Response> => {
  const { data } = await api({
    method: 'delete',
    url: `/app/spourgiti/admin/user-favorites/lists/${DeleteFavoriteListDTO.listId}`,
  })
  return data
}
export const useDeleteUserFavoritesListMutation = (
  options: Omit<
    UseMutationOptions<Response, AxiosError, DeleteFavoriteListDTO>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(
    MutationKeys.removeFavoritesList,
    deleteUserFavoritesList,
    {
      ...options,
    } as Omit<
      UseMutationOptions<Response, AxiosError, DeleteFavoriteListDTO>,
      'mutationKey' | 'mutationFn'
    >
  )
}

const ListService = {
  countByCriteria,
  searchByCriteria,
  getUserFavoritesList,
  getUserFavoritesListPropertyAds,
  addToUserFavoritesList,
  removeFromUserFavoritesList,
  deleteUserFavoritesList,
  updateList,
  hooks: {
    useList,
    useListPropertyAds,
    useCountByCriteriaMutation,
    useSearchByCriteriaMutation,
    useGetUserFavoritesListMutation,
    useGetUserFavoritesListPropertyAdsMutation,
    useRemoveFromUserFavoritesListMutation,
    useAddToUserFavoritesList,
    useDeleteUserFavoritesListMutation,
    useUpdateList,
  },
  keys: { MutationKeys, QueryKeys },
}

export default ListService
